/**
 * Mock data + navigation - port 1:1 z new-design/data.jsx.
 *
 * Tool IDs (slugs np. 'home-staging') sa display-only - mapping na DB variant_type
 * jest w dolnej czesci pliku (TOOL_DB_VARIANT). DB nadal uzywa stare wartosci
 * (virtual_staging, decluttering, etc) - decyzja 2026-06-07 brak migracji schemy.
 */
import {
  type LightingProfileDefinition,
  type RetouchOptionDefinition,
  type VariantType,
} from "@adresflow-v2/shared";

export type ToolKind = "upload" | "remont" | "stepper" | "wizard" | "zabudowa" | "dzialka" | "wycena" | "kw";

export interface ToolDef {
  id: string;             // display slug (np. "home-staging")
  name: string;
  eyebrow: string;        // uppercase eyebrow text
  action: string;
  icon: string;           // Icon name (matches src/components/Icon)
  accent: string;
  accent2: string;
  desc: string;
  long: string;
  cost: number;
  costLabel: string;
  kind: ToolKind;
  variants?: string[];
  /**
   * Profile światła do wyboru przed generacją (narzędzia retuszujące).
   * Brak pola = narzędzie nie pyta o światło i nie pokazuje tego kroku.
   */
  lightingProfiles?: readonly LightingProfileDefinition[];
  /**
   * Przełączniki „co poprawiamy". Brak pola = narzędzie robi jedną rzecz i nie ma
   * czego przełączać.
   */
  retouchOptions?: readonly RetouchOptionDefinition[];
}

export const TOOLS: ToolDef[] = [
  {
    id: "home-staging",
    name: "Home Staging",
    eyebrow: "HOME STAGING AI",
    action: "Posprzątaj i postaw na sprzedaż",
    icon: "wand",
    accent: "#8b5cf6",
    accent2: "#7c3aed",
    desc: "Sprząta, ustawia meble, dodaje oświetlenie. Zdjęcie jak z okładki.",
    long: "Porządkuję — usuwam bałagan, robię home staging i doświetlam. Do 10 zdjęć w 60 sekund.",
    cost: 3,
    costLabel: "3 kredyty / zdjęcie",
    kind: "upload",
    variants: ["Jasny ☀️", "Ciepły 🔥", "Neutralny"],
  },
  // Między Home Stagingiem a Pustym pokojem świadomie: to jest wariant „nie ruszaj
  // mojego wnętrza" dla agenta, który ma umeblowane mieszkanie i chce tylko porządne
  // zdjęcie. Opis musi tę granicę stawiać wprost, bo trzy sąsiednie kafelki zaczynają
  // się od słowa „sprząta".
  {
    id: "sprzatanie",
    name: "Sprzątanie i retusz",
    eyebrow: "SPRZĄTANIE AI",
    action: "Popraw zdjęcie, nie wnętrze",
    icon: "brush",
    accent: "#8b5cf6",
    accent2: "#7c3aed",
    desc: "Sprząta bałagan, prostuje piony, doświetla i kadruje. Meble i wystrój zostają.",
    long: "Usuwam bałagan, kable i zbędne przedmioty, prostuję piony oraz wyrównuję światło jak fotograf wnętrz. Meble i wystrój zostają dokładnie takie same — bez dodawania obcych dekoracji.",
    cost: 3,
    costLabel: "3 kredyty / zdjęcie",
    kind: "upload",
  },
  {
    id: "czysty-pokoj",
    name: "Pusty pokój",
    eyebrow: "PUSTY POKÓJ AI",
    action: "Pokaż samą przestrzeń",
    icon: "eraser",
    accent: "#8b5cf6",
    accent2: "#7c3aed",
    desc: "Usuwa wszystkie meble i bałagan. Zostaje tylko architektura.",
    long: "Usuwam meble i dekoracje — zostaje czysta architektura wnętrza. Łazienki zachowują armaturę.",
    cost: 1,
    costLabel: "1 kredyt / zdjęcie",
    kind: "upload",
    variants: ["Pusty pokój"],
  },
  {
    id: "remont",
    name: "Wirtualny remont",
    eyebrow: "WIRTUALNY REMONT AI",
    action: "Urządź pusty pokój",
    icon: "sofa",
    accent: "#8b5cf6",
    accent2: "#7c3aed",
    desc: "Wgraj pusty pokój, wybierz styl — AI wstawi meble i dekoracje.",
    long: "Urządzam puste pomieszczenie w wybranym stylu wnętrza — meble, tekstylia, dekoracje.",
    cost: 3,
    costLabel: "3 kredyty / zdjęcie",
    kind: "remont",
  },
  {
    id: "wizualizacja",
    name: "Rzut 3D z kartki",
    eyebrow: "RZUT 3D AI",
    action: "Zamień szkic w render 3D",
    icon: "ruler",
    accent: "#8b5cf6",
    accent2: "#7c3aed",
    desc: "Wgraj rzut lub odręczny szkic — AI wygeneruje render 3D mieszkania.",
    long: "Trzyetapowy pipeline: szkic → kolorowy plan 2D → render 3D, etap po etapie.",
    // Musi zgadzać się z TOOL_COSTS.floor_plan_visualization (test toolCosts).
    cost: 3,
    costLabel: "3 kredyty / etap",
    kind: "stepper",
  },
  {
    id: "zabudowa",
    name: "Zabudowa działek",
    eyebrow: "ZABUDOWA DZIAŁEK AI",
    action: "Pokaż potencjał działki",
    icon: "building",
    accent: "#8b5cf6",
    accent2: "#7c3aed",
    desc: "Wgraj zdjęcie pustej działki — AI pokaże gotową zabudowę.",
    long: "Wizualizuję zabudowę na pustej działce — dom lub budynek w wybranym stylu, wkomponowany w teren i otoczenie.",
    // Koszt musi zgadzać się z API (PLOT_COST=5, REFRESH_COST=1 w plot-step.routes.ts).
    cost: 5,
    costLabel: "5 kredytów / wizualizacja · 1 kr poprawka",
    kind: "zabudowa",
  },
  {
    id: "zaznacz-dzialke",
    name: "Zaznacz działkę",
    eyebrow: "ZAZNACZANIE DZIAŁEK",
    action: "Zaznacz granice i opisz działkę",
    icon: "ruler",
    accent: "#f59e0b",
    accent2: "#d97706",
    desc: "Obrys granic działki na zdjęciu + tabliczka z adresem, ceną i parametrami.",
    long: "Zaznacz granice działki na swoim zdjęciu i pobierz grafikę do oferty — z tabliczką (adres, nr działki, cena, MPZP) albo bez. Bez AI, za darmo.",
    cost: 0,
    costLabel: "Za darmo",
    kind: "dzialka",
  },
  {
    id: "wycena",
    name: "Wycena nieruchomości",
    eyebrow: "ANALIZA RYNKU",
    action: "Sprawdź realne ceny transakcyjne",
    icon: "growth",
    accent: "#10b981",
    accent2: "#059669",
    desc: "Realne ceny z aktów notarialnych (RCN, geoportal) — mediana zł/m², histogram, transakcje.",
    long: "Wpisz adres — pobieram realne ceny transakcyjne z rejestru państwowego (RCiWN). Mediana zł/m², rozkład cen i lista transakcji w okolicy. Za darmo.",
    cost: 0,
    costLabel: "Za darmo",
    kind: "wycena",
  },
  {
    id: "ksiegi-wieczyste",
    name: "Księgi wieczyste",
    eyebrow: "REJESTR EKW",
    action: "Sprawdź księgę wieczystą",
    icon: "shield",
    accent: "#0ea5e9",
    accent2: "#0284c7",
    desc: "Wpisz numer KW — pobieram dane wprost z portalu EKW. Treści nie przechowujemy.",
    long: "Sprawdzenie księgi wieczystej bez zakładania oferty. Dane z portalu EKW na żywo; u nas zostaje wyłącznie numer, żeby dało się wrócić do tej samej księgi jednym kliknięciem.",
    cost: 0,
    costLabel: "Za darmo",
    kind: "kw",
  },
  // Zdjęcia biznesowe na końcu listy — świadoma decyzja produktowa (kafelek
  // najmniej związany z ofertą nieruchomości).
  {
    id: "zespol",
    name: "Zdjęcia biznesowe",
    eyebrow: "ZDJĘCIA BIZNESOWE AI",
    action: "Pokaż profesjonalny zespół",
    icon: "camera",
    accent: "#8b5cf6",
    accent2: "#7c3aed",
    desc: "Profesjonalne portrety i zdjęcia całego zespołu agencji.",
    long: "Generuję profesjonalne zdjęcie portretowe lub całego zespołu — Ty wybierasz styl i tło.",
    cost: 5,
    costLabel: "5 kredytów / zdjęcie",
    kind: "wizard",
  },
];

export const toolById = (id: string): ToolDef | undefined => TOOLS.find((t) => t.id === id);

// KW_HUB_CARD usunięte: kafelek „Sprawdź księgę wieczystą" prowadził na /oferta/dna
// i po wydzieleniu KW do własnego narzędzia dublował się z wpisem w TOOLS — dwa
// kafelki na hubie, oba na ten sam ekran, przy czym opis starego obiecywał
// utworzenie oferty, czego nowy ekran świadomie nie robi.

/**
 * Mapowanie display slug -> DB variant_type (processing_jobs.tool_type).
 * DB ma stare wartosci (decyzja 2026-06-07 brak migracji schemy).
 */
export const TOOL_DB_VARIANT: Record<string, VariantType> = {
  "home-staging": "virtual_staging",
  "sprzatanie": "room_cleanup",
  "czysty-pokoj": "decluttering",
  "remont": "empty_room_arrangement",
  "wizualizacja": "arranged",
  "zespol": "team_photo",
  "zabudowa": "plot_visualization",
};

/** Reverse mapping: DB variant_type -> display slug */
export const DB_VARIANT_TO_SLUG: Record<string, string> = Object.entries(TOOL_DB_VARIANT).reduce(
  (acc, [slug, variant]) => ({ ...acc, [variant]: slug }),
  {} as Record<string, string>,
);

// =====================================================
// Style picker options per tool
// =====================================================

export interface StyleOption {
  id: string;
  name: string;
  desc: string;
}

export const INTERIOR_STYLES: StyleOption[] = [
  { id: "nowoczesny", name: "Nowoczesny", desc: "czyste linie, minimalizm" },
  { id: "skandynawski", name: "Skandynawski", desc: "jasne drewno, biel, natura" },
  { id: "industrialny", name: "Industrialny", desc: "cegła, metal, beton" },
  { id: "klasyczny", name: "Klasyczny", desc: "elegancja, symetria" },
  { id: "morski", name: "Morski", desc: "błękit, biel, len" },
  { id: "japandi", name: "Japandi", desc: "spokój, ciepłe minimum" },
  { id: "boho", name: "Boho", desc: "tkaniny, rośliny, ziemia" },
  { id: "artdeco", name: "Art Deco", desc: "złoto, geometria, glamour" },
  { id: "rustykalny", name: "Rustykalny", desc: "drewno, kamień, ciepło" },
  { id: "darklux", name: "Dark Luxury", desc: "głębokie barwy, mosiądz" },
];

/**
 * Palety stylów wnętrz — trzy płaskie barwy charakterystyczne dla stylu.
 *
 * Wcześniej były to gradienty rozciągnięte na kafel 16:10 i wyglądały dokładnie
 * jak niezaładowane zdjęcia (uwaga wspólnika 10.09: „te style wyglądają, jakby
 * były niezaładowane"). Płaskie pasma czytają się jako świadome oznaczenie,
 * mieszczą się w chipie obok nazwy i skracają listę stylów o dwie trzecie —
 * dzięki czemu opis własny wraca nad krawędź ekranu.
 */
export const STYLE_PALETTES: Record<string, [string, string, string]> = {
  nowoczesny: ["#3a3f47", "#8b929c", "#d7dbe0"],
  skandynawski: ["#f3efe7", "#d9c7a8", "#b7936a"],
  industrialny: ["#4a4038", "#7a6f63", "#a9a29a"],
  klasyczny: ["#f4ecdd", "#cdae7a", "#8a6d3b"],
  morski: ["#eaf3f7", "#8fc0d6", "#2f6f8f"],
  japandi: ["#e9e4da", "#b8a98f", "#6e5f4a"],
  boho: ["#efe0cd", "#c99b6f", "#7a5a3a"],
  artdeco: ["#1b1b22", "#8a6d3b", "#e6c079"],
  rustykalny: ["#e3d3bd", "#a97e56", "#5f4326"],
  darklux: ["#15151c", "#3a2f2a", "#a67c52"],
  stodola: ["#e7e2da", "#9a8f80", "#4b463f"],
  tradycyjny: ["#e9cdb0", "#b8703e", "#6e3d24"],
  dworek: ["#f4efe4", "#cdbb95", "#7d6a45"],
  minimal: ["#fdfdfd", "#e2e2e4", "#b9babe"],
  srodziemno: ["#f6efe2", "#dab98d", "#2f7fa0"],
};

const NEUTRAL_PALETTE: [string, string, string] = ["#d7dbe0", "#8b929c", "#3a3f47"];

export const stylePalette = (id: string): [string, string, string] => STYLE_PALETTES[id] ?? NEUTRAL_PALETTE;

export const ROOM_TYPES: string[] = [
  "Salon", "Sypialnia", "Kuchnia", "Salon z aneksem", "Gabinet / biuro",
  "Pokój dziecięcy", "Pokój młodzieżowy", "Łazienka", "Jadalnia", "Garderoba / pralnia",
];

export const ZABUDOWA_TYPES: string[] = [
  "Dom jednorodzinny", "Dom parterowy", "Dom z poddaszem", "Nowoczesna willa",
  "Bliźniak", "Zabudowa szeregowa", "Budynek wielorodzinny", "Budynek usługowy",
];

export const BUILDING_STYLES: StyleOption[] = [
  { id: "nowoczesny", name: "Nowoczesny", desc: "płaski dach, duże okna" },
  { id: "stodola", name: "Stodoła", desc: "dwuspadowy dach, prosta bryła" },
  { id: "tradycyjny", name: "Tradycyjny", desc: "cegła, dachówka" },
  { id: "dworek", name: "Dworek", desc: "kolumny, symetria" },
  { id: "minimal", name: "Minimalistyczny", desc: "biała bryła, kubatura" },
  { id: "skandynawski", name: "Skandynawski", desc: "drewno, biel, ciepło" },
  { id: "srodziemno", name: "Śródziemnomorski", desc: "jasny tynk, taras" },
  { id: "klasyczny", name: "Klasyczny", desc: "detal, elegancja" },
];

export const TEAM_MOODS: string[] = [
  "C-Level / Formalne", "Business Casual", "Tech Startup",
  "Creative Agency", "Smart Casual",
];

export const PHOTO_STYLES: string[] = [
  "Candid", "Motion", "Posed", "Cinematic",
  "Editorial", "Documentary", "Creative",
];

export const BODY_TYPES: string[] = ["Szczupła", "Średnia", "Atletyczna", "Pełna"];

/**
 * Zdjęcia biznesowe — nowe etykiety wg uwag testera 2026-07-06:
 * „Nastrój zdjęcia" → „Ogólny styl ubioru" (dopiski w nawiasach),
 * „Styl fotografii" → „Charakter zdjęcia" (polskie, opisowe labelki jak w v1).
 * `value` idzie do promptu backendu (opisowy string / enum), `label` widzi użytkownik.
 */
export interface DressCode { value: string; label: string; hint: string }
export const TEAM_DRESS_CODES: DressCode[] = [
  { value: "formal C-level business attire (tailored suits and dresses)", label: "C-Level / Formalny", hint: "Garnitury, garsonki" },
  { value: "business casual (modern office)", label: "Business Casual", hint: "Nowoczesne biuro" },
  { value: "relaxed tech startup casual", label: "Tech Startup", hint: "Luźny biznes" },
  { value: "creative and artistic", label: "Creative / Art", hint: "Kreatywny" },
  { value: "smart casual everyday", label: "Smart Casual", hint: "Codzienny" },
];

export interface PhotoCharacter { value: string; label: string }
export const PHOTO_CHARACTERS: PhotoCharacter[] = [
  { value: "candid", label: "Skradziony moment (rozmowa, naturalne, nie patrzy w aparat)" },
  { value: "motion", label: "W ruchu (idący, gestykulujący, reportaż)" },
  { value: "posed", label: "Klasyczny portret (pozowane, prosto w obiektyw)" },
  { value: "cinematic", label: "Kinowy kadr (dramatyczne światło, głębia ostrości)" },
  { value: "editorial", label: "Editorial / Magazyn (modowy, wyraziste światło)" },
  { value: "documentary", label: "Dokumentalny (surowy, realistyczny)" },
  { value: "creative", label: "Kreatywny (nietypowe kąty, artystyczna wizja)" },
];

// =====================================================
// Credit packs (one-shot top-up)
// =====================================================

export interface CreditPack {
  id: string;
  name: string;
  cr: number;          // credits
  price: number;       // PLN
  valid: string;
  rec: boolean;        // recommended
}

export const CREDIT_PACKS: CreditPack[] = [
  { id: "starter", name: "Mały",   cr: 50,   price: 49,  valid: "3 miesiące",   rec: false },
  { id: "pro",     name: "Średni", cr: 200,  price: 149, valid: "6 miesięcy",   rec: true  },
  { id: "studio",  name: "Duży",   cr: 600,  price: 389, valid: "12 miesięcy",  rec: false },
  { id: "mega",    name: "Hurt",   cr: 1500, price: 879, valid: "12 miesięcy",  rec: false },
];

// =====================================================
// Plans (subscription model w mockup - v2 ma top-up only, ale UI moze pokazac)
// =====================================================

export interface PlanFeature {
  t: string;
  on: boolean;
}

export interface PlanDef {
  id: string;
  name: string;
  tagline: string;
  audience: "individual" | "business";
  free?: boolean;
  priceM: number;
  priceY: number;
  creditsM: number;
  seats: string;
  accent: string;
  rec?: boolean;
  features: PlanFeature[];
}

// PLANS (literały cenowe) usunięte 2026-08-21 — cennik czyta wyłącznie
// GET /v1/payments/plans przez usePlans(). Definicja rozjechała się z bazą
// kompletnie: pokazywała Agenta za 99 zł z 60 kredytami (jest 49 zł i 50 kr),
// Biuro za 299 zł (jest 99 zł) i plan „Agencja", którego nie ma.
// PlanDef zostaje jako kształt karty — wypełnia go planMapper.ts.


// =====================================================
// Brand kit (Marka)
// =====================================================

export interface BrandPalette {
  id: string;
  name: string;
  colors: [string, string, string];
}

export const BRAND_PALETTES: BrandPalette[] = [
  { id: "fiolet",   name: "Fioletowy", colors: ["#8b5cf6", "#d946ef", "#0a0b10"] },
  { id: "granat",   name: "Granatowy", colors: ["#2563eb", "#06b6d4", "#0b1220"] },
  { id: "zielen",   name: "Szmaragd",  colors: ["#10b981", "#84cc16", "#08130f"] },
  { id: "bursztyn", name: "Bursztyn",  colors: ["#f59e0b", "#ef4444", "#140d06"] },
  { id: "grafit",   name: "Grafit",    colors: ["#64748b", "#94a3b8", "#0b0e14"] },
];

// Kolejność = wizualny układ 3×3 (góra/środek/dół × lewo/środek/prawo).
// Współdzielone przez BrandPage (LogoTab) — jedno źródło prawdy pozycji znaku wodnego.
export const WM_POSITIONS = [
  { id: "tl", label: "Lewy górny" },
  { id: "tc", label: "Środek górny" },
  { id: "tr", label: "Prawy górny" },
  { id: "c",  label: "Środek" },
  { id: "bl", label: "Lewy dolny" },
  { id: "bc", label: "Środek dolny" },
  { id: "br", label: "Prawy dolny" },
];

/**
 * Przykładowe dane agenta — WYŁĄCZNIE jako `placeholder` w formularzu /konto?tab=dane.
 * Nie wstawiaj ich jako wartości pól ani do DEFAULT_BRAND: są nie do odróżnienia od
 * danych użytkownika, trafiały do bazy przy zapisie i na stopki ofert klientów
 * (patrz komentarz przy DEFAULT_BRAND w hooks/useBrand.ts).
 */
export const BRAND_AGENT_DEFAULT = {
  name: "Marek Kowalski",
  title: "Doradca ds. nieruchomości",
  phone: "+48 601 234 567",
  email: "marek@bnk.pl",
  license: "Lic. 12345",
  agency: "Biuro Nieruchomości Kowalski",
};

// =====================================================
// Two-level navigation
// =====================================================

// "pricing" = /cennik — jedyny cennik (plany abonamentowe + paczki kredytów).
// Rozdzielenie z 2026-08-04 na dwa cenniki zwinięte 2026-08-22; "plans" zostaje
// w unii, bo /cennik-abonament wciąż przekierowuje i mapuje się na tę nazwę.
export type RouteName = "hub" | "tool" | "valuations" | "gallery" | "project" | "offer" | "photos" | "brand" | "agency" | "account" | "pricing" | "plans" | "admin";

export interface NavRoute {
  name: RouteName;
  toolId?: string;
  projectId?: string;
  tab?: string;
  filter?: string;
}

export interface NavItem {
  label: string;
  icon: string;
  route: NavRoute;
  accent?: string;
}

export interface NavSection {
  id: string;
  label: string;
  icon: string;
  accent?: string;
  items: NavItem[];
}

export const NAV: NavSection[] = [
  {
    id: "studio", label: "Studio", icon: "wand",
    items: [
      { label: "Wszystkie narzędzia", icon: "layers", route: { name: "hub" } },
      // „Moje wyceny" NIE jest tu pozycją menu, mimo że ma własną trasę: Studio to
      // lista NARZĘDZI, a zapisana wycena jest wynikiem jednego z nich. Osobny wpis
      // obok dziewięciu narzędzi mieszał dwa porządki — „czym mogę coś zrobić"
      // z „co już zrobiłem". Wejście na listę żyje w nagłówku samego narzędzia
      // (WycenaScreen) i w podsumowaniu przepływu.
      ...TOOLS.map<NavItem>((t) => ({
        label: t.name,
        icon: t.icon,
        route: { name: "tool", toolId: t.id },
        accent: t.accent,
      })),
    ],
  },
  {
    id: "galeria", label: "Oferty", icon: "folder",
    items: [
      { label: "Nowa oferta", icon: "plus", route: { name: "offer" }, accent: "#10b981" },
      // „Archiwum" usunięte 2026-07-21 — backend nie zna archiwizacji ofert
      // (brak `archived` w Project i w migracjach), więc pozycja pokazywała
      // dokładnie tę samą listę co „Wszystkie oferty".
      { label: "Wszystkie oferty", icon: "folder", route: { name: "gallery" } },
    ],
  },
  {
    id: "zdjecia", label: "Galeria", icon: "image",
    items: [
      { label: "Wszystkie zdjęcia", icon: "image", route: { name: "photos" } },
      { label: "Bez przypisania", icon: "layers", route: { name: "photos", filter: "unassigned" } },
    ],
  },
  {
    // „Marka" nie jest osobną sekcją — logo, kolory i stopka to ustawienia
    // agencji, więc żyją tutaj razem z zespołem i zużyciem kredytów.
    // Dane agenta przeniesione do sekcji Konto (2026-07-21) — to informacje
    // o użytkowniku, nie o wyglądzie marki.
    id: "agencja", label: "Agencja", icon: "users",
    items: [
      { label: "Dane biura", icon: "settings", route: { name: "agency", tab: "dane" } },
      { label: "Zespół", icon: "users", route: { name: "agency" } },
      { label: "Zużycie kredytów", icon: "coins", route: { name: "agency", tab: "zuzycie" } },
      { label: "Logo i znak wodny", icon: "image", route: { name: "brand", tab: "logo" } },
      { label: "Kolory marki", icon: "brush", route: { name: "brand", tab: "kolory" } },
      { label: "Stopka ogłoszenia", icon: "split", route: { name: "brand", tab: "stopka" } },
    ],
  },
  {
    // Uporządkowane 2026-07-21: „Saldo i historia" oraz „Dane agenta" wskazywały
    // wcześniej ten sam route (świeciły razem), a „Rozliczenia" prowadziły na
    // ?tab=billing, którego AccountPage w ogóle nie czytała. Teraz każda pozycja
    // to osobny widok /konto, a rozliczenia obsługuje „Plany i kredyty" (/cennik).
    // Kolejność 2026-07-23 (uwaga wspólnika #6): Dane → Saldo i historia → Plany i kredyty.
    id: "konto", label: "Konto", icon: "settings",
    items: [
      { label: "Dane klienta", icon: "user", route: { name: "account", tab: "dane" } },
      { label: "Saldo i historia", icon: "refresh", route: { name: "account" } },
      // Wejście do portalu Stripe (karta, faktury, anulowanie, liczba agentów).
      // Osobna pozycja, a nie tylko przycisk w kaflu salda: klient szukający
      // „gdzie zmienić kartę" idzie do menu, nie skanuje kafli.
      { label: "Subskrypcja", icon: "coins", route: { name: "account", tab: "subskrypcja" } },
      // Jeden cennik od 2026-08-22: /cennik pokazuje plany i paczki naraz. Pozycja
      // zostaje widoczna dla każdego — sama strona pilnuje gate'a płatności
      // (useCreditPurchase), więc nie-admin widzi ofertę bez przycisków zakupu.
      { label: "Cennik", icon: "coins", route: { name: "pricing" } },
    ],
  },
  {
    // Panel admina — sekcje jako 3. poziom w lewym SubNav (jak sekcje oferty),
    // nie poziomy pasek w treści. Widoczne tylko dla adminów: RailNav ma osobny
    // przycisk „Admin" w stopce, a tę sekcję filtruje z głównej listy railu
    // (żeby nie dublować). AdminPage czyta aktywną zakładkę z `?tab=`.
    // „Statystyki" bez `tab` = domyślny widok /admin (DEFAULT_TAB.admin="stats").
    id: "admin", label: "Panel admina", icon: "settings",
    items: [
      { label: "Biznes 360°", icon: "chart", route: { name: "admin" } },
      { label: "Agencje", icon: "users", route: { name: "admin", tab: "agencies" } },
      { label: "Użytkownicy", icon: "search", route: { name: "admin", tab: "users" } },
      { label: "Oferty", icon: "image", route: { name: "admin", tab: "projects" } },
      { label: "Onboarding", icon: "user", route: { name: "admin", tab: "onboarding" } },
      { label: "Ankieta", icon: "chat", route: { name: "admin", tab: "feedback" } },
      { label: "Zapytania", icon: "megaphone", route: { name: "admin", tab: "waitlist" } },
      { label: "Polecenia", icon: "growth", route: { name: "admin", tab: "referrals" } },
      { label: "Roadmapa", icon: "check", route: { name: "admin", tab: "roadmap" } },
      { label: "Blog", icon: "split", route: { name: "admin", tab: "blog" } },
      { label: "Generacje", icon: "image", route: { name: "admin", tab: "generations" } },
    ],
  },
];

export const ROUTE_SECTION: Record<RouteName, string> = {
  hub: "studio", tool: "studio", valuations: "studio",
  gallery: "galeria", project: "galeria", offer: "galeria",
  photos: "zdjecia",
  brand: "agencja",
  agency: "agencja",
  account: "konto", pricing: "konto", plans: "konto",
  admin: "admin",
};

export const SECTION_DEFAULT: Record<string, NavRoute> = {
  studio: { name: "hub" },
  galeria: { name: "gallery" },
  zdjecia: { name: "photos" },
  agencja: { name: "agency" },
  // Awatar w railu prowadzi do sekcji „Konto" — ma otwierać konto, nie cennik
  // (wylogowanie żyje pod /konto?tab=dane, więc /cennik oddalało je o klik).
  konto: { name: "account" },
  admin: { name: "admin" },
};

export const navSection = (id: string): NavSection | undefined => NAV.find((s) => s.id === id);

// =====================================================
// Photo subjects (per tool gallery labels)
// =====================================================

export const PHOTO_SUBJECTS: Record<string, string[]> = {
  "home-staging": ["salon", "sypialnia", "kuchnia", "jadalnia", "gabinet", "przedpokój"],
  "sprzatanie": ["salon", "sypialnia", "kuchnia", "łazienka", "hol"],
  "czysty-pokoj": ["pokój dzienny", "sypialnia", "kuchnia", "łazienka", "hol"],
  "remont": ["pusty salon", "pusta sypialnia", "aneks kuchenny"],
  "wizualizacja": ["rzut mieszkania", "plan piętra", "układ pokoi"],
  "zespol": ["portret agenta", "zespół agencji", "zdjęcie biznesowe"],
  "zabudowa": ["pusta działka", "działka budowlana", "teren pod zabudowę"],
};

/**
 * Hub Before/After preview images per tool - skopiowane z v1 adresflow/public/studio-previews/.
 * Real zdjecia ilustrujace co AI robi. Hub HubPage uzywa do BeforeAfter slider w tile cards.
 *
 * Mapping na display slug. Jezeli tool nie ma w mapie -> Hub renderuje Ph placeholder fallback.
 *
 * home-staging i czysty-pokoj uzywaja tego samego "czysty-pokoj" folder (najprostszy use case
 * - usuwanie balaganu wystarcza zeby zwizualizowac oba narzedzia).
 */
export const TOOL_PREVIEW_IMAGES: Record<string, { before: string; after: string }> = {
  // Własna para — NIE współdziel z „czysty-pokoj". Home Staging ma pokazywać pokój
  // UMEBLOWANY, a podglądy declutteringu kończą się pustym wnętrzem, więc oba kafle
  // wyglądały identycznie i przeczyły opisowi („ustawia meble").
  "home-staging": {
    before: "/studio-previews/home-staging/przed.webp",
    after: "/studio-previews/home-staging/po.jpg",
  },
  "sprzatanie": {
    before: "/studio-previews/sprzatanie/po.jpg",
    after: "/studio-previews/sprzatanie/po.jpg",
  },
  "czysty-pokoj": {
    before: "/studio-previews/czysty-pokoj/przed.webp",
    after: "/studio-previews/czysty-pokoj/po.jpg",
  },
  "remont": {
    before: "/studio-previews/remont/remont_przed.webp",
    after: "/studio-previews/remont/remont_po.jpg",
  },
  "wizualizacja": {
    before: "/studio-previews/rzut-3d/przed.webp",
    after: "/studio-previews/rzut-3d/po.jpg",
  },
  "zespol": {
    before: "/studio-previews/biznesowe/przed.webp",
    after: "/studio-previews/biznesowe/po.webp",
  },
  // Zabudowa ma tylko jedno zdjęcie poglądowe (v1 też nie miał pary przed/po) —
  // `before` wskazuje na ten sam plik, bo kafel w Hubie renderuje wyłącznie `after`.
  "zabudowa": {
    before: "/studio-previews/dzialka/cover.jpg",
    after: "/studio-previews/dzialka/cover.jpg",
  },
  // „Zaznacz działkę" pokazuje gotowy efekt narzędzia: obrys granic + tabliczka
  // z parametrami. Jak przy zabudowie — jedno zdjęcie, bo pary „przed/po" tu nie ma
  // (kafel w Hubie renderuje wyłącznie `after`). Do tej pory ten kafel był jedynym
  // z pustym placeholderem, więc wyglądał na niedokończony.
  "zaznacz-dzialke": {
    before: "/studio-previews/zaznacz-dzialke/cover.webp",
    after: "/studio-previews/zaznacz-dzialke/cover.webp",
  },
  // Wycena i KW nie mają pary „przed/po" (to analiza danych, nie generowanie
  // zdjęcia) — kafel pokazuje jedno zdjęcie poglądowe zamiast pustego placeholdera.
  "wycena": {
    before: "/studio-previews/wycena/cover.webp",
    after: "/studio-previews/wycena/cover.webp",
  },
  // Grafika po dawnej karcie hubowej KW — bez tego wpisu kafelek renderuje szary
  // placeholder obok dopracowanych okładek pozostałych narzędzi. KW nie ma pary
  // „przed/po" (to odczyt danych, nie generowanie zdjęcia), więc `before` i `after`
  // celowo wskazują ten sam plik — slider nie ma czego pokazać.
  "ksiegi-wieczyste": {
    before: "/studio-previews/ksiega-wieczysta/cover.webp",
    after: "/studio-previews/ksiega-wieczysta/cover.webp",
  },
};
